import { appendAmendment, appendEvent, sealChain } from "./chain";
import { suggestedCategory, suggestedNotifications } from "./notifications";
import { Incident, IncidentDocumentation, IncidentEvent, IncidentType } from "./types";

const emptyDoc = (): IncidentDocumentation => ({});

function hoursAgo(now: Date, hours: number): string {
  return new Date(now.getTime() - hours * 3600000).toISOString();
}

function minutesAgo(now: Date, minutes: number): string {
  return new Date(now.getTime() - minutes * 60000).toISOString();
}

function event(
  id: string,
  type: IncidentEvent["type"],
  at: string,
  actor: string,
  name: string,
  payload: Record<string, string> = {}
): IncidentEvent {
  return { id, type, at, actor_user_id: actor, actor_name: name, payload };
}

function summary(type: IncidentType, camera: string, place: string): string {
  const label =
    type === "unsafe_interaction" || type === "responsive_behaviour"
      ? "Responsive Behaviour / Abuse"
      : type === "fall"
        ? "fall"
        : type === "wandering_zone"
          ? "wandering or exit-seeking"
          : "other";
  return `NeuraVue camera ${camera} flagged a ${label} alert at ${place}. The alert was sent to the assigned staff. This is not a finding.`;
}

export function buildSeedIncidents(now: Date): Incident[] {
  const awaiting: Incident = {
    id: "NV-INC-2026-000201",
    facility_id: "site-lakeside",
    facility_name: "Example Lakeside LTC Home",
    home_licence: "EXAMPLE-0000",
    org_id: "org-example",
    alert_id: "alert-awaiting-fall",
    type: "fall",
    status: "awaiting_staff_review",
    province: "ON",
    resident: { id: "res-c", display_name: "Resident C", room: "309" },
    room: "309",
    unit: "3 East",
    location: "3 East, room 309, bathroom doorway",
    camera_id: "3E-309",
    created_at: minutesAgo(now, 40),
    alert_at: minutesAgo(now, 40),
    alert_summary: summary("fall", "3E-309", "3 East, room 309, bathroom doorway"),
    alert_subtype: "Fall detected",
    notified_staff: [
      { name: "RPN L. Example", role: "RPN", at: minutesAgo(now, 40) },
      { name: "PSW T. Example", role: "PSW", at: minutesAgo(now, 40) }
    ],
    documentation: emptyDoc(),
    reasonable_grounds_decision: { decision: null },
    notifications: [],
    events: [
      event("e-201-1", "alert_created", minutesAgo(now, 40), "system", "NeuraVue", {
        note: "Alert recorded. Documentation has not started."
      }),
      event("e-201-2", "notification_sent", minutesAgo(now, 40), "system", "NeuraVue", {
        note: "Assigned staff were notified."
      })
    ],
    amendments: [],
    post_fall_instrument_default: "Example Falls Tool 2.1",
    model_version: "neuravue-detect-2026.10",
    clip_ref: { id: "clip-201", retention: "short" }
  };

  const fall: Incident = {
    id: "NV-INC-2026-000124",
    facility_id: "site-lakeside",
    facility_name: "Example Lakeside LTC Home",
    home_licence: "EXAMPLE-0000",
    alert_id: "alert-fall-124",
    type: "fall",
    status: "in_documentation",
    province: "ON",
    resident: { id: "res-c", display_name: "Resident C", room: "309" },
    room: "309",
    unit: "3 East",
    location: "3 East, room 309, bathroom doorway",
    camera_id: "3E-309",
    created_at: hoursAgo(now, 4),
    alert_at: hoursAgo(now, 4),
    alert_summary: summary("fall", "3E-309", "3 East, room 309, bathroom doorway"),
    alert_subtype: "Fall detected",
    notified_staff: [
      { name: "RPN L. Example", role: "RPN", at: hoursAgo(now, 4) },
      { name: "PSW T. Example", role: "PSW", at: hoursAgo(now, 4) }
    ],
    opened_by: "RPN L. Example",
    opened_at: hoursAgo(now, 3.9),
    arrived_at: hoursAgo(now, 3.8),
    confirmed_by: "user-nurse-1",
    confirmed_by_name: "RPN L. Example",
    confirmed_at: hoursAgo(now, 3.5),
    documentation: {
      resident_name: "Resident C",
      event_time: hoursAgo(now, 4),
      discovered_at: hoursAgo(now, 3.8),
      location: "3 East, room 309, bathroom doorway",
      post_fall_instrument: "Example Falls Tool 2.1",
      critical_incident_category: suggestedCategory("fall")
    },
    reasonable_grounds_decision: { decision: null },
    notifications: [],
    events: [
      event("e-124-1", "alert_created", hoursAgo(now, 4), "system", "NeuraVue"),
      event("e-124-2", "alert_opened", hoursAgo(now, 3.9), "user-nurse-1", "RPN L. Example"),
      event("e-124-3", "arrived_on_scene", hoursAgo(now, 3.8), "user-nurse-1", "RPN L. Example", {
        note: "I'm with the resident"
      }),
      event("e-124-4", "alert_confirmed", hoursAgo(now, 3.5), "user-nurse-1", "RPN L. Example", {
        note: "Staff confirmed the alert. Documentation started. Nothing was submitted."
      })
    ],
    amendments: [],
    post_fall_instrument_default: "Example Falls Tool 2.1",
    model_version: "neuravue-detect-2026.10",
    clip_ref: { id: "clip-124", start: hoursAgo(now, 4), retention: "short" }
  };
  fall.notifications = suggestedNotifications(fall);

  const unsafe: Incident = {
    id: "NV-INC-2026-000123",
    facility_id: "site-lakeside",
    facility_name: "Example Lakeside LTC Home",
    home_licence: "EXAMPLE-0000",
    alert_id: "alert-ui-123",
    type: "unsafe_interaction",
    status: "in_documentation",
    province: "ON",
    resident: { id: "res-a", display_name: "Resident A", room: "214" },
    room: "214",
    unit: "2 West",
    location: "2 West, room 214, near bed 2",
    camera_id: "2W-214",
    created_at: hoursAgo(now, 15),
    alert_at: hoursAgo(now, 15),
    alert_summary: summary("unsafe_interaction", "2W-214", "2 West, room 214, near bed 2"),
    alert_subtype: "Possible pushing, hitting, or grabbing",
    notified_staff: [
      { name: "RPN J. Example", role: "RPN", at: hoursAgo(now, 15) },
      { name: "RN M. Example", role: "RN in charge", at: hoursAgo(now, 15) }
    ],
    opened_by: "RPN J. Example",
    opened_at: hoursAgo(now, 14.8),
    arrived_at: hoursAgo(now, 14.6),
    confirmed_by: "user-nurse-1",
    confirmed_by_name: "RPN J. Example",
    confirmed_at: hoursAgo(now, 14),
    documentation: {
      resident_name: "Resident A",
      resident_confirmed: true,
      event_time: hoursAgo(now, 15),
      event_time_confirmed: true,
      discovered_at: hoursAgo(now, 14.6),
      discovered_at_confirmed: true,
      location: "2 West, room 214, near bed 2",
      location_confirmed: true,
      responders_confirmed: true,
      witnessed: "no",
      observed:
        "Resident A was sitting on the edge of the bed, holding the left forearm. Resident B was standing beside the bed.",
      leading_events: "Night check. Resident B had walked into the shared room.",
      resident_statement: 'Resident A said: "They grabbed my arm."',
      other_residents: "Resident B, other resident in the room. File number EX-214B.",
      persons_present: "PSW K. Example arrived with me. No other staff were in the room.",
      distress: "Tearful and frightened. Asked to have Resident B moved.",
      injuries: "Reddened area on the left forearm. No open wound.",
      pain: "Numeric scale 4/10 at the forearm.",
      consciousness: "Alert, same as baseline.",
      safety_measures: "Residents separated. Increased checks for the rest of the night.",
      care_given: "Arm looked at by RPN J. Example. Ice offered. Resident A declined.",
      hospital_transfer: "no",
      critical_incident_category: suggestedCategory("unsafe_interaction"),
      critical_incident_category_confirmed: true
    },
    reasonable_grounds_decision: {
      decision: "yes",
      suspicion:
        "I suspect unwanted physical contact from another resident, based on what I saw and what Resident A said.",
      decided_by: "user-nurse-1",
      decided_by_name: "RPN J. Example",
      decided_at: hoursAgo(now, 14)
    },
    notifications: [],
    events: [
      event("e-123-1", "alert_created", hoursAgo(now, 15), "system", "NeuraVue"),
      event("e-123-2", "alert_opened", hoursAgo(now, 14.8), "user-nurse-1", "RPN J. Example"),
      event("e-123-3", "arrived_on_scene", hoursAgo(now, 14.6), "user-nurse-1", "RPN J. Example", {
        note: "I'm with the resident"
      }),
      event("e-123-4", "alert_confirmed", hoursAgo(now, 14), "user-nurse-1", "RPN J. Example"),
      event("e-123-5", "reasonable_grounds_recorded", hoursAgo(now, 14), "user-nurse-1", "RPN J. Example", {
        decision: "yes"
      })
    ],
    amendments: [],
    model_version: "neuravue-detect-2026.10",
    clip_ref: { id: "clip-123", retention: "hold" }
  };
  unsafe.notifications = suggestedNotifications(unsafe);

  const pending: Incident = {
    id: "NV-INC-2026-000188",
    facility_id: "site-lakeside",
    facility_name: "Example Lakeside LTC Home",
    home_licence: "EXAMPLE-0000",
    alert_id: "alert-ui-false",
    type: "unsafe_interaction",
    status: "false_alert_pending_confirm",
    province: "ON",
    resident: { id: "res-e", display_name: "Resident E", room: "118" },
    room: "118",
    unit: "1 West",
    location: "1 West, room 118",
    camera_id: "1W-118",
    created_at: minutesAgo(now, 25),
    alert_at: minutesAgo(now, 25),
    alert_summary: summary("unsafe_interaction", "1W-118", "1 West, room 118"),
    alert_subtype: "Possible pushing, hitting, or grabbing",
    notified_staff: [{ name: "RPN J. Example", role: "RPN", at: minutesAgo(now, 25) }],
    opened_by: "RPN J. Example",
    opened_at: minutesAgo(now, 22),
    arrived_at: minutesAgo(now, 20),
    locked_at: minutesAgo(now, 18),
    locked_by: "user-nurse-1",
    locked_by_name: "RPN J. Example",
    documentation: emptyDoc(),
    reasonable_grounds_decision: { decision: null },
    notifications: [],
    false_alert: {
      reason_code: "visitor_or_staff",
      note: "A visitor was helping the resident sit up. I saw no force and no distress.",
      risk_situation: "no",
      concern: "no",
      dismissed_by: "user-nurse-1",
      dismissed_by_name: "RPN J. Example",
      dismissed_at: minutesAgo(now, 18)
    },
    events: [
      event("e-188-1", "alert_created", minutesAgo(now, 25), "system", "NeuraVue"),
      event("e-188-2", "alert_opened", minutesAgo(now, 22), "user-nurse-1", "RPN J. Example"),
      event("e-188-3", "false_alert_marked", minutesAgo(now, 18), "user-nurse-1", "RPN J. Example", {
        reason_code: "visitor_or_staff"
      })
    ],
    amendments: [],
    model_version: "neuravue-detect-2026.10",
    clip_ref: { id: "clip-188", retention: "short" }
  };

  const qc: Incident = {
    id: "NV-INC-2026-000130",
    facility_id: "site-riviere",
    facility_name: "CHSLD Exemple-Rivière",
    alert_id: "alert-qc-130",
    type: "unsafe_interaction",
    status: "in_documentation",
    province: "QC",
    resident: { id: "res-d", display_name: "Usager D", room: "12" },
    room: "12",
    unit: "Unité 2",
    location: "Unité 2, chambre 12",
    camera_id: "2-12",
    created_at: hoursAgo(now, 2),
    alert_at: hoursAgo(now, 2),
    alert_summary: summary("unsafe_interaction", "2-12", "Unité 2, chambre 12"),
    alert_subtype: "Possible pushing, hitting, or grabbing",
    notified_staff: [{ name: "Inf. A. Exemple", role: "Infirmière", at: hoursAgo(now, 2) }],
    opened_by: "Inf. A. Exemple",
    opened_at: hoursAgo(now, 1.8),
    confirmed_by: "user-nurse-2",
    confirmed_by_name: "Inf. A. Exemple",
    confirmed_at: hoursAgo(now, 1.5),
    documentation: {
      resident_name: "Usager D",
      resident_confirmed: true,
      event_time: hoursAgo(now, 2),
      event_time_confirmed: true,
      discovered_at: hoursAgo(now, 1.8),
      discovered_at_confirmed: true,
      location: "Unité 2, chambre 12",
      location_confirmed: true,
      observed: "Usager D était assis au lit. Un autre usager était près du lit. Aucune blessure ouverte visible.",
      distress: "Agité, a demandé de l'aide.",
      critical_incident_category: suggestedCategory("unsafe_interaction")
    },
    reasonable_grounds_decision: {
      decision: "yes",
      suspicion: "Motifs raisonnables de croire à un geste non désiré, d'après ce que j'ai vu en arrivant.",
      decided_by: "user-nurse-2",
      decided_by_name: "Inf. A. Exemple",
      decided_at: hoursAgo(now, 1.5)
    },
    notifications: [],
    events: [
      event("e-130-1", "alert_created", hoursAgo(now, 2), "system", "NeuraVue"),
      event("e-130-2", "alert_confirmed", hoursAgo(now, 1.5), "user-nurse-2", "Inf. A. Exemple"),
      event("e-130-3", "reasonable_grounds_recorded", hoursAgo(now, 1.5), "user-nurse-2", "Inf. A. Exemple", {
        decision: "yes"
      })
    ],
    amendments: [],
    model_version: "neuravue-detect-2026.10",
    clip_ref: { id: "clip-130", retention: "hold" }
  };
  qc.notifications = suggestedNotifications(qc);

  const locked: Incident = {
    id: "NV-INC-2026-000099",
    facility_id: "site-lakeside",
    facility_name: "Example Lakeside LTC Home",
    home_licence: "EXAMPLE-0000",
    alert_id: "alert-locked-099",
    type: "fall",
    status: "submitted_locked",
    province: "ON",
    resident: { id: "res-f", display_name: "Resident F", room: "402" },
    room: "402",
    unit: "4 East",
    location: "4 East, room 402, bedside",
    camera_id: "4E-402",
    created_at: hoursAgo(now, 72),
    alert_at: hoursAgo(now, 72),
    alert_summary: summary("fall", "4E-402", "4 East, room 402, bedside"),
    alert_subtype: "Fall detected",
    notified_staff: [{ name: "RPN L. Example", role: "RPN", at: hoursAgo(now, 72) }],
    confirmed_by: "user-nurse-1",
    confirmed_by_name: "RPN L. Example",
    confirmed_at: hoursAgo(now, 71),
    locked_at: hoursAgo(now, 70),
    locked_by: "user-nurse-1",
    locked_by_name: "RPN L. Example",
    documentation: {
      resident_name: "Resident F",
      resident_confirmed: true,
      event_time: hoursAgo(now, 72),
      event_time_confirmed: true,
      discovered_at: hoursAgo(now, 71.5),
      discovered_at_confirmed: true,
      location: "4 East, room 402, bedside",
      location_confirmed: true,
      responders_confirmed: true,
      prefilled_confirmed: true,
      witnessed: "yes",
      witnessed_by: "PSW T. Example",
      observed: "Resident F was on the floor beside the bed, on the left side, awake.",
      leading_events: "Getting up without the call bell.",
      persons_present: "PSW T. Example",
      consciousness: "Alert, baseline.",
      vitals: "BP 128/76, HR 82, RR 18, temp 36.6, SpO2 97% on room air. Taken on arrival.",
      pain: "Numeric scale 2/10 at the left hip.",
      injuries: "Small bruise on the left hip.",
      post_fall_instrument: "Example Falls Tool 2.1",
      post_fall_instrument_confirmed: true,
      post_fall_completed_at: hoursAgo(now, 70.5),
      post_fall_findings: "No head strike. Neuro checks started per home policy.",
      care_given: "Helped back to bed with two staff. Physician informed.",
      hospital_transfer: "no",
      immediate_prevention: "Bed alarm on. Call bell clipped to clothing.",
      long_term_actions: "Physiotherapy referral. Care plan review this week.",
      care_plan_updated: "Yes, by RPN L. Example the same shift.",
      outcome: "Resting in bed. Pain 1/10 at handover.",
      critical_incident_category: "Fall without transfer to hospital",
      critical_incident_category_confirmed: true,
      attestation_accepted: true,
      attestation_name: "L. Example",
      attestation_designation: "RPN",
      attestation_at: hoursAgo(now, 70),
      attestation_user_id: "user-nurse-1",
      late_entry: true,
      late_entry_reason: "Record was finalized after assessments and notifications were completed across the shift.",
      official_channel: "Not sent. Staff will copy into the home chart.",
      official_submitter: "L. Example",
      supervisor_review: {
        by: "user-doc-1",
        name: "M. Example",
        designation: "Director of care",
        at: hoursAgo(now, 48),
        note: "Reviewed. Separate from the nurse attestation. I did not co-sign the nurse entry."
      }
    },
    reasonable_grounds_decision: {
      decision: "no",
      decided_by: "user-nurse-1",
      decided_by_name: "RPN L. Example",
      decided_at: hoursAgo(now, 71)
    },
    notifications: [],
    events: [
      event("e-099-1", "alert_created", hoursAgo(now, 72), "system", "NeuraVue"),
      event("e-099-2", "alert_confirmed", hoursAgo(now, 71), "user-nurse-1", "RPN L. Example"),
      event("e-099-3", "documentation_submitted", hoursAgo(now, 70), "user-nurse-1", "RPN L. Example", {
        note: "Record finalized and locked. Not sent to a regulator."
      }),
      event("e-099-4", "supervisor_reviewed", hoursAgo(now, 48), "user-doc-1", "M. Example", {
        note: "Director of care review. Not a co-signature."
      })
    ],
    amendments: [
      {
        id: "amd-099-1",
        incident_id: "NV-INC-2026-000099",
        author_user_id: "user-nurse-1",
        author_name: "RPN L. Example",
        at: hoursAgo(now, 60),
        reason: "Pain score was written in the wrong box.",
        changes: [
          { field: "Pain", old: "Numeric scale 5/10 at the left hip.", new: "Numeric scale 2/10 at the left hip." }
        ]
      }
    ],
    post_fall_instrument_default: "Example Falls Tool 2.1",
    model_version: "neuravue-detect-2026.10",
    clip_ref: { id: "clip-099", retention: "short" }
  };
  locked.notifications = suggestedNotifications(locked).map((row) =>
    row.party === "charge_nurse" || row.party === "physician" || row.party === "sdm_family"
      ? {
          ...row,
          done: true,
          person_name: row.party === "physician" ? "Dr. Example" : "Family Member F",
          at: hoursAgo(now, 70.2),
          by: "RPN L. Example",
          method: "phone" as const,
          recorded_by: "user-nurse-1"
        }
      : row
  );
  if (locked.notifications[0]) {
    locked.notifications[0].person_name = "RN M. Example";
  }

  const dismissed: Incident = {
    id: "NV-INC-2026-000150",
    facility_id: "site-lakeside",
    facility_name: "Example Lakeside LTC Home",
    alert_id: "alert-dismissed-150",
    type: "fall",
    status: "closed",
    province: "ON",
    resident: { id: "res-g", display_name: "Resident G", room: "220" },
    room: "220",
    unit: "2 East",
    location: "2 East, room 220",
    camera_id: "2E-220",
    created_at: hoursAgo(now, 24 * 5),
    alert_at: hoursAgo(now, 24 * 5),
    alert_summary: summary("fall", "2E-220", "2 East, room 220"),
    alert_subtype: "Fall detected",
    notified_staff: [{ name: "PSW T. Example", role: "PSW", at: hoursAgo(now, 24 * 5) }],
    locked_at: hoursAgo(now, 24 * 5),
    locked_by: "user-nurse-1",
    locked_by_name: "RPN J. Example",
    documentation: emptyDoc(),
    reasonable_grounds_decision: { decision: null },
    notifications: [],
    false_alert: {
      reason_code: "normal_activity",
      note: "Resident was reaching for a blanket. No fall. No distress.",
      risk_situation: "no",
      concern: "no",
      dismissed_by: "user-nurse-1",
      dismissed_by_name: "RPN J. Example",
      dismissed_at: hoursAgo(now, 24 * 5)
    },
    events: [
      event("e-150-1", "alert_created", hoursAgo(now, 24 * 5), "system", "NeuraVue"),
      event("e-150-2", "false_alert_marked", hoursAgo(now, 24 * 5), "user-nurse-1", "RPN J. Example", {
        reason_code: "normal_activity"
      }),
      event("e-150-3", "closed", hoursAgo(now, 24 * 5), "user-nurse-1", "RPN J. Example")
    ],
    amendments: [],
    model_version: "neuravue-detect-2026.10",
    clip_ref: { id: "clip-150", retention: "short" }
  };

  const dismissedUi: Incident = {
    id: "NV-INC-2026-000151",
    facility_id: "site-lakeside",
    facility_name: "Example Lakeside LTC Home",
    alert_id: "alert-dismissed-151",
    type: "unsafe_interaction",
    status: "closed",
    province: "ON",
    resident: { id: "res-h", display_name: "Resident H", room: "105" },
    room: "105",
    unit: "1 East",
    location: "1 East, room 105",
    camera_id: "1E-105",
    created_at: hoursAgo(now, 24 * 3),
    alert_at: hoursAgo(now, 24 * 3),
    alert_summary: summary("unsafe_interaction", "1E-105", "1 East, room 105"),
    alert_subtype: "Possible pushing, hitting, or grabbing",
    notified_staff: [{ name: "RPN J. Example", role: "RPN", at: hoursAgo(now, 24 * 3) }],
    locked_at: hoursAgo(now, 24 * 3),
    locked_by: "user-doc-1",
    locked_by_name: "M. Example",
    documentation: emptyDoc(),
    reasonable_grounds_decision: { decision: null },
    notifications: [],
    false_alert: {
      reason_code: "normal_activity",
      note: "Staff were transferring with a belt. Expected contact.",
      risk_situation: "no",
      concern: "no",
      dismissed_by: "user-nurse-1",
      dismissed_by_name: "RPN J. Example",
      dismissed_at: hoursAgo(now, 24 * 3),
      confirmation: {
        by: "user-doc-1",
        by_name: "M. Example",
        role: "Director of care",
        at: hoursAgo(now, 24 * 3 - 0.4),
        decision: "confirm",
        note: "I agree this was a planned transfer. This is not a co-signature of the nurse note."
      }
    },
    events: [
      event("e-151-1", "alert_created", hoursAgo(now, 24 * 3), "system", "NeuraVue"),
      event("e-151-2", "false_alert_marked", hoursAgo(now, 24 * 3), "user-nurse-1", "RPN J. Example"),
      event("e-151-3", "false_alert_confirmed", hoursAgo(now, 24 * 3 - 0.4), "user-doc-1", "M. Example", {
        decision: "confirm"
      }),
      event("e-151-4", "closed", hoursAgo(now, 24 * 3 - 0.4), "user-doc-1", "M. Example")
    ],
    amendments: [],
    model_version: "neuravue-detect-2026.10",
    clip_ref: { id: "clip-151", retention: "short" }
  };

  const wander: Incident = {
    id: "NV-INC-2026-000160",
    facility_id: "site-lakeside",
    facility_name: "Example Lakeside LTC Home",
    alert_id: "alert-wander-160",
    type: "wandering_zone",
    status: "in_documentation",
    province: "ON",
    resident: { id: "res-i", display_name: "Resident I", room: "330" },
    room: "330",
    unit: "3 West",
    location: "3 West, east stairwell door",
    camera_id: "3W-EAST",
    created_at: hoursAgo(now, 5),
    alert_at: hoursAgo(now, 5),
    alert_summary: summary("wandering_zone", "3W-EAST", "3 West, east stairwell door"),
    alert_subtype: "Zone exit: east stairwell",
    notified_staff: [{ name: "RN M. Example", role: "RN", at: hoursAgo(now, 5) }],
    confirmed_by: "user-nurse-1",
    confirmed_by_name: "RN M. Example",
    confirmed_at: hoursAgo(now, 4.5),
    documentation: {
      resident_name: "Resident I",
      resident_confirmed: true,
      event_time: hoursAgo(now, 5),
      location: "3 West, east stairwell door",
      missing_last_seen: hoursAgo(now, 5),
      critical_incident_category: suggestedCategory("wandering_zone")
    },
    reasonable_grounds_decision: { decision: "no", decided_by: "user-nurse-1", decided_at: hoursAgo(now, 4.5) },
    notifications: [],
    events: [
      event("e-160-1", "alert_created", hoursAgo(now, 5), "system", "NeuraVue"),
      event("e-160-2", "alert_confirmed", hoursAgo(now, 4.5), "user-nurse-1", "RN M. Example")
    ],
    amendments: [],
    model_version: "neuravue-detect-2026.10",
    clip_ref: { id: "clip-160", retention: "hold" }
  };
  wander.notifications = suggestedNotifications(wander);

  return [awaiting, fall, unsafe, pending, qc, locked, dismissed, dismissedUi, wander].map((incident) =>
    sealChain(incident)
  );
}

export function blankDocumentation(incident: Incident): IncidentDocumentation {
  return {
    resident_name: incident.resident?.display_name || "",
    event_time: incident.alert_at,
    discovered_at: incident.confirmed_at,
    location: incident.location || "",
    post_fall_instrument: incident.type === "fall" ? incident.post_fall_instrument_default || "" : undefined,
    critical_incident_category: suggestedCategory(incident.type),
    responders_confirmed: false
  };
}

export { appendAmendment, appendEvent };
